import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { ComplianceSection } from '../compliance.component';

export interface SelectedSectionsToolbarAction {
  type: 'generate-reports' | 'cancel' | 'clear-selection';
  sections?: ComplianceSection[];
  forceRecompute?: boolean;
}

/** Floating toolbar that appears when compliance sections are selected, offering actions like "Generate Report" or "Clear Selection". */
@Component({
  selector: 'app-selected-sections-toolbar',
  imports: [FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, MatCheckboxModule, MatChipsModule],
  templateUrl: './selected-sections-toolbar.component.html',
  styleUrls: ['./selected-sections-toolbar.component.scss'],
})
export class SelectedSectionsToolbarComponent implements OnInit, OnChanges {
  @Input() selectedSections: ComplianceSection[] = [];
  @Input() visible = false;
  @Output() action = new EventEmitter<SelectedSectionsToolbarAction>();

  internalVisible = false;
  forceRecompute = false;

  ngOnInit(): void {
    this.updateVisibility();
  }

  ngOnChanges(): void {
    this.updateVisibility();
  }

  private updateVisibility(): void {
    this.internalVisible = this.visible && this.selectedSections.length > 0;
  }

  onGenerateComplianceReports(): void {
    if (this.selectedSections.length > 0) {
      this.action.emit({
        type: 'generate-reports',
        sections: this.selectedSections,
        forceRecompute: this.forceRecompute,
      });
    }
  }

  onCancel(): void {
    this.action.emit({
      type: 'cancel',
    });
  }

  onClearSelection(): void {
    this.action.emit({
      type: 'clear-selection',
    });
  }

  getSelectedSectionsText(): string {
    const count = this.selectedSections.length;
    return count === 1 ? '1 section selected' : `${count} sections selected`;
  }

  getSelectedSectionNames(): string {
    return this.selectedSections
      .map(section => (section.title.length > 30 ? section.title.substring(0, 30) + '...' : section.title))
      .join(', ');
  }

  getSectionSlug(section: ComplianceSection): string {
    // Extract the section slug from the sectionId (e.g., CABC2022P4_Ch03_Sec302.1)
    return section.sectionId || section.title;
  }

  onRemoveSection(section: ComplianceSection): void {
    // Emit an action to remove this specific section
    const remainingSections = this.selectedSections.filter(s => s.sectionId !== section.sectionId);
    this.action.emit({
      type: 'clear-selection',
      sections: remainingSections,
    });
  }
}
